import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { usePaymentsStore } from "@/store/usePaymentsStore";
import {
  buildWebPurchaseRedemptionUrl,
  closeWebPurchaseRedemptionDialog,
  openWebPurchaseRedemptionDialog,
} from "@/services/paywall";
import { WebPurchaseRedemptionDialogContent } from "@/components/WebPurchaseRedemptionDialogContent";

/** Match / beat RC UI and in-app overlays (Radix, etc.); stay within 32-bit signed int. */
const WEB_PAYWALL_Z = 2_147_483_647;

/**
 * Renders the RevenueCat web paywall phone shell via a portal. Mount once near the app root
 * (e.g. next to `usePayments()` in `App.tsx`).
 */
export function PaywallHost() {
  const session = usePaymentsStore((s) => s.webPaywallHostSession);
  const redemptionSession = usePaymentsStore(
    (s) => s.webPurchaseRedemptionSession
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const resolvedSessionIdRef = useRef<number | null>(null);

  /** Browser-only: `?redemption_token=` opens the redemption dialog for UX testing. */
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const redemptionToken = new URLSearchParams(window.location.search).get(
      "redemption_token"
    );
    if (!redemptionToken) return;
    openWebPurchaseRedemptionDialog(
      buildWebPurchaseRedemptionUrl(redemptionToken)
    );
  }, []);

  useLayoutEffect(() => {
    if (!session || !panelRef.current) return;
    if (resolvedSessionIdRef.current === session.sessionId) return;
    resolvedSessionIdRef.current = session.sessionId;
    session.resolvePanel(panelRef.current);
  }, [session]);

  useLayoutEffect(() => {
    if (!session) {
      resolvedSessionIdRef.current = null;
    }
  }, [session]);

  /**
   * Lock scroll while the paywall is up. Clear with `removeProperty` on teardown so we never
   * restore a stale value after Radix Sheet / another modal has already updated `overflow`.
   */
  useEffect(() => {
    if (!session && !redemptionSession) return;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      if (
        usePaymentsStore.getState().webPaywallHostSession ||
        usePaymentsStore.getState().webPurchaseRedemptionSession
      ) {
        return;
      }
      document.documentElement.style.removeProperty("overflow");
      document.body.style.removeProperty("overflow");
    };
  }, [session, redemptionSession]);

  const redemptionDialog = redemptionSession
    ? createPortal(
        <div
          className="pointer-events-auto fixed inset-0 isolate touch-none"
          style={{ zIndex: WEB_PAYWALL_Z }}
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-black/50" aria-hidden />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pt-[max(12px,env(safe-area-inset-top,0px))] pr-[max(12px,env(safe-area-inset-right,0px))] pb-[max(12px,env(safe-area-inset-bottom,0px))] pl-[max(12px,env(safe-area-inset-left,0px))]">
            <div className="pointer-events-auto relative max-h-dialog-safe w-dialog-safe max-w-dialog-safe min-h-0 overflow-y-auto overscroll-contain rounded-lg border bg-background p-6 shadow-lg">
              <button
                type="button"
                className="absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100"
                aria-label="Close"
                onClick={closeWebPurchaseRedemptionDialog}
              >
                <XIcon className="size-4" />
              </button>
              <WebPurchaseRedemptionDialogContent
                onClose={closeWebPurchaseRedemptionDialog}
              />
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  const webPaywallPortal =
    session && !Capacitor.isNativePlatform()
      ? createPortal(
          <div
            className="pointer-events-auto fixed inset-0 isolate overflow-hidden overscroll-contain touch-none"
            style={{ zIndex: WEB_PAYWALL_Z }}
            role="presentation"
          >
            <div
              className="pointer-events-auto absolute inset-0 z-0 bg-slate-900/78"
              aria-hidden
            />
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center pt-[max(12px,env(safe-area-inset-top,0px))] pr-[max(12px,env(safe-area-inset-right,0px))] pb-[max(12px,env(safe-area-inset-bottom,0px))] pl-[max(12px,env(safe-area-inset-left,0px))]">
              <div
                ref={panelRef}
                className="pointer-events-auto relative max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-24px))] w-full min-h-0 max-w-[390px] touch-auto overflow-auto overscroll-contain rounded-2xl bg-white shadow-2xl [-webkit-overflow-scrolling:touch]"
              />
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {redemptionDialog}
      {webPaywallPortal}
    </>
  );
}
